import { Component, EventEmitter, inject, Input, OnDestroy, OnChanges, Output, SimpleChanges } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { TabsModule } from 'primeng/tabs';
import { Subscription } from 'rxjs';
import { HelpCenterService, HelpManualResponse } from '../../help-center/help-center.service';

@Component({
  selector: 'app-page-help-dialog',
  standalone: true,
  imports: [ButtonModule, DialogModule, TabsModule],
  templateUrl: './page-help-dialog.component.html',
  styleUrls: ['./page-help-dialog.component.css']
})
export class PageHelpDialogComponent implements OnChanges, OnDestroy {
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Input() pageTitle = '';
  @Input() manualId = '';
  @Input() videoId = '';
  @Input() checklist: string[] = [];

  activeTab: 'guide' | 'video' = 'guide';
  guide: HelpManualResponse | null = null;
  loading = false;
  error = false;
  videoUrl: SafeResourceUrl | null = null;

  private readonly helpService = inject(HelpCenterService);
  private readonly sanitizer = inject(DomSanitizer);
  private guideRequest?: Subscription;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['videoId']) {
      this.videoUrl = /^[A-Za-z0-9_-]{11}$/.test(this.videoId)
        ? this.sanitizer.bypassSecurityTrustResourceUrl(`https://www.youtube-nocookie.com/embed/${this.videoId}`)
        : null;
      if (!this.videoUrl) this.activeTab = 'guide';
    }
    if (changes['visible']?.currentValue && !changes['visible']?.previousValue) {
      this.activeTab = this.manualId ? 'guide' : 'video';
      this.loadGuide();
    }
    if (changes['visible']?.previousValue && !changes['visible']?.currentValue) {
      this.activeTab = 'guide';
      this.guideRequest?.unsubscribe();
      this.loading = false;
    }
  }

  ngOnDestroy(): void {
    this.guideRequest?.unsubscribe();
  }

  close(): void {
    this.activeTab = 'guide';
    this.visibleChange.emit(false);
  }

  onTabChange(value: string | number): void {
    this.activeTab = value === 'video' && this.videoUrl ? 'video' : 'guide';
  }

  openGuideLink(event: MouseEvent): void {
    const anchor = event.target instanceof Element ? event.target.closest('a') : null;
    if (anchor) {
      anchor.target = '_blank';
      anchor.rel = 'noopener noreferrer';
    }
  }

  private loadGuide(): void {
    this.guideRequest?.unsubscribe();
    this.guide = null;
    this.error = false;
    this.loading = !!this.manualId;
    if (!this.manualId) return;
    this.guideRequest = this.helpService.getManual(this.manualId).subscribe({
      next: guide => {
        this.guide = guide;
        this.loading = false;
      },
      error: () => {
        this.error = true;
        this.loading = false;
      }
    });
  }
}
